import { Text } from "@overmap-ai/blocks"
import type { IssueType, Stored } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { formatTimestampDate } from "../../../utils/date"
import { IssueTypeIcon } from "../../Icons/IssueTypeIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useIssueTypeTableColumns = () => {
	return useMemo<ColumnDef<Stored<IssueType>>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				enableHiding: true,
				meta: {
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const issueType = row.original

					return (
						<div className="flex items-center gap-2">
							<IssueTypeIcon issueTypeId={issueType.uuid} background={false} />
							<Text>{issueType.name}</Text>
						</div>
					)
				},
			},
			{
				id: "submitted_at",
				accessorFn: (issueType) => issueType.submitted_at,
				meta: {
					plainHeader: "Created at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
		],
		[],
	)
}

export const useIssueTypeTable = (args: Omit<TableOptions<Stored<IssueType>>, "getCoreRowModel">) => {
	return useReactTable<Stored<IssueType>>({
		...args,
		getRowId: (originalRow) => originalRow.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
